import { useState } from "react";
import { Button, Icon, Input, Popup, Table } from "semantic-ui-react";
import { fmtDateTime } from "../adminHelpers";
import { formatRupees, isMoney } from "../../../utils/money";

/* Room rent: one row per room and rate. Each row says how its days were
   counted — the 24-hour blocks from that room's check-in — so an admin can
   answer "why 2 days?" from the screen. */
export default function BillRoomCharges({ bill, editable, busy, onSetRate, onResetRate }) {
  const rows = bill.lines.filter((l) => l.kind === "ROOM");
  if (!rows.length) return <p style={{ opacity: 0.7 }}>No room charges — no room on this booking has a stay to bill.</p>;
  return (
    <Table compact celled unstackable size="small">
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>Room</Table.HeaderCell>
          <Table.HeaderCell>Check-in</Table.HeaderCell>
          <Table.HeaderCell>Check-out</Table.HeaderCell>
          <Table.HeaderCell textAlign="right">Days</Table.HeaderCell>
          <Table.HeaderCell textAlign="right">Rate / day</Table.HeaderCell>
          <Table.HeaderCell textAlign="right">Amount</Table.HeaderCell>
          <Table.HeaderCell textAlign="right">CGST</Table.HeaderCell>
          <Table.HeaderCell textAlign="right">SGST</Table.HeaderCell>
          <Table.HeaderCell textAlign="right">Total</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {rows.map((l) => (
          <Table.Row key={l.id}>
            <Table.Cell>
              {l.description.replace(/ × .*$/, "")}
              {l.detail.moved && <div style={{ fontSize: "0.8em", opacity: 0.7 }}>Moved rooms during the stay</div>}
            </Table.Cell>
            <Table.Cell>{fmtDateTime(l.detail.stay_start)}</Table.Cell>
            <Table.Cell>
              {fmtDateTime(l.detail.stay_end)}
              {l.detail.estimated && <div style={{ fontSize: "0.8em", color: "#b35c00" }}>Expected — not checked out yet</div>}
            </Table.Cell>
            <Table.Cell textAlign="right">
              {Number(l.quantity)}
              <Popup
                trigger={<Icon name="info circle" color="grey" style={{ marginLeft: 4 }} aria-label="How the days were counted" />}
                content={`Counted in 24-hour blocks from check-in, with ${l.detail.grace_minutes ?? bill.graceMinutes} minutes' grace before another day starts. This room's whole stay is ${l.detail.stay_days} day(s).`}
              />
            </Table.Cell>
            <Table.Cell textAlign="right">
              <RateCell line={l} editable={editable} busy={busy} onSetRate={onSetRate} onResetRate={onResetRate} />
            </Table.Cell>
            <Table.Cell textAlign="right">{formatRupees(l.taxable)}</Table.Cell>
            <Table.Cell textAlign="right">{formatRupees(l.cgst)}</Table.Cell>
            <Table.Cell textAlign="right">{formatRupees(l.sgst)}</Table.Cell>
            <Table.Cell textAlign="right"><strong>{formatRupees(l.total)}</strong></Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );
}

function RateCell({ line, editable, busy, onSetRate, onResetRate }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String(Number(line.unitPrice)));
  const valid = isMoney(value);

  if (editing) {
    return (
      <span style={{ display: "inline-flex", gap: 4 }}>
        <Input size="mini" style={{ width: 90 }} value={value} error={!valid} aria-label="New rate per day"
          onChange={(_, d) => setValue(d.value)} />
        <Button size="mini" primary disabled={!valid || busy}
          onClick={async () => { if (await onSetRate(line.id, value)) setEditing(false); }}>Save</Button>
        <Button size="mini" basic onClick={() => setEditing(false)}>Cancel</Button>
      </span>
    );
  }
  return (
    <span>
      {formatRupees(line.unitPrice)}
      {line.rateOverridden && (
        <div style={{ fontSize: "0.8em", color: "#b35c00" }}>
          Set by hand (standard {formatRupees(line.detail.standard_rate)})
          {editable && (
            <Button size="mini" basic compact style={{ marginLeft: 4 }} disabled={busy}
              onClick={() => onResetRate(line.id)}>Use standard</Button>
          )}
        </div>
      )}
      {editable && !line.rateOverridden && (
        <Button size="mini" basic compact style={{ marginLeft: 6 }} disabled={busy}
          onClick={() => { setValue(String(Number(line.unitPrice))); setEditing(true); }}>Change</Button>
      )}
    </span>
  );
}
